import { Injectable } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { BehaviorSubject } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { File } from "../models/file";
import { FileService } from "./file.service";

export type SplitPreviewTab = {
  id: string;
  file: File;
  url: string | null;
  safeUrl: SafeResourceUrl | null;
  loading: boolean;
};

export type SplitPreviewState = {
  visible: boolean;
  activeTabId: string | null;
  tabs: SplitPreviewTab[];
};

const initialState: SplitPreviewState = {
  visible: false,
  activeTabId: null,
  tabs: [],
};

@Injectable({
  providedIn: "root",
})
export class SplitPreviewService {
  private stateSubject = new BehaviorSubject<SplitPreviewState>(initialState);
  state$ = this.stateSubject.asObservable();

  constructor(
    private fileService: FileService,
    private sanitizer: DomSanitizer,
    private toast: ToastrService
  ) {}

  async openFiles(files: File[]) {
    if (!files?.length) {
      return;
    }
    let state = this.stateSubject.getValue();
    let tabs = [...state.tabs];
    let activeTabId = state.activeTabId;

    for (const file of files) {
      const tabId = this.getTabId(file);
      const existing = tabs.find((t) => t.id === tabId);
      if (existing) {
        this.revokeTabUrl(existing);
        tabs = tabs.map((t) =>
          t.id === tabId
            ? { ...t, file, url: null, safeUrl: null, loading: true }
            : t
        );
      } else {
        tabs.push({
          id: tabId,
          file,
          url: null,
          safeUrl: null,
          loading: true,
        });
      }
      activeTabId = tabId;
      this.stateSubject.next({ visible: true, activeTabId, tabs: [...tabs] });
      this.loadTab(tabId, file);
    }
  }

  openFile(file: File) {
    if (file) {
      this.openFiles([file]);
    }
  }

  closeTab(tabId: string) {
    const state = this.stateSubject.getValue();
    const tab = state.tabs.find((t) => t.id === tabId);
    if (tab) {
      this.revokeTabUrl(tab);
    }
    const tabs = state.tabs.filter((t) => t.id !== tabId);
    const activeTabId =
      state.activeTabId === tabId
        ? tabs[tabs.length - 1]?.id ?? null
        : state.activeTabId;
    const visible = tabs.length > 0;
    this.stateSubject.next({ visible, activeTabId, tabs });
  }

  setActiveTab(tabId: string) {
    const state = this.stateSubject.getValue();
    if (!state.tabs.find((t) => t.id === tabId)) {
      return;
    }
    this.stateSubject.next({ ...state, activeTabId: tabId, visible: true });
  }

  close() {
    const state = this.stateSubject.getValue();
    state.tabs.forEach((t) => this.revokeTabUrl(t));
    this.stateSubject.next(initialState);
  }

  private getTabId(file: File): string {
    const id = (file as any)?.id;
    if (id !== undefined && id !== null) {
      return `file-${id}`;
    }
    const name = file?.nome || "file";
    return `tmp-${name}-${Date.now()}`;
  }

  private async loadTab(tabId: string, file: File) {
    try {
      const blob = await this.fetchBlob(file);
      if (!blob) {
        throw new Error("Nessun contenuto disponibile");
      }
      const url = URL.createObjectURL(blob);
      const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `${url}#toolbar=1&navpanes=0`
      );
      this.patchTab(tabId, (tab) => {
        this.revokeTabUrl(tab);
        return { ...tab, url, safeUrl, loading: false };
      });
    } catch (e) {
      console.error(e);
      this.toast.error("Impossibile aprire il file in split view.", "Errore");
      this.patchTab(tabId, (tab) => ({ ...tab, loading: false }));
    }
  }

  private patchTab(
    tabId: string,
    updater: (tab: SplitPreviewTab) => SplitPreviewTab
  ) {
    const state = this.stateSubject.getValue();
    const tabs = state.tabs.map((t) => (t.id === tabId ? updater(t) : t));
    this.stateSubject.next({ ...state, tabs });
  }

  private revokeTabUrl(tab: SplitPreviewTab) {
    if (tab?.url) {
      URL.revokeObjectURL(tab.url);
    }
  }

  private async fetchBlob(file: File): Promise<Blob | null> {
    // .msg files: use dedicated HTML preview endpoint
    if (file.nome && file.nome.toLowerCase().endsWith(".msg")) {
      return await this.fileService.openMsgPreview(file).toPromise();
    }

    try {
      return await this.fileService.open(file, true).toPromise();
    } catch (e) {
      console.warn("Versione PDF non disponibile, apro originale", e);
      this.toast.info("Versione PDF non disponibile, apro il file originale.");
      try {
        return await this.fileService.open(file, false).toPromise();
      } catch (err) {
        console.error("Impossibile aprire il file originale", err);
        throw err;
      }
    }
  }
}
